// CORE
import { useAppSelector } from 'hooks/redux';
import useKickoffA2AFlow from 'hooks/useKickoffA2AFlow';
import { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import { DateTime } from 'luxon';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createSelector } from 'reselect';

import { a2aTransfersActions } from 'app-components/flux/a2aTransfers';
// Data
import type { accountsTypes } from 'app-components/flux/accounts';
import { accountsActions, accountsSelectors } from 'app-components/flux/accounts';
import { actionHelpers } from 'app-components/flux/core';
import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { profileSelectors } from 'app-components/flux/profile';
import { loadState, useLoadState } from 'app-components/hooks/utils';
import { numberUtils } from 'app-components/utils/number';
import * as fundingAccountsActions from 'data/fundingAccounts/actions';
import * as fundingAccountsSelectors from 'data/fundingAccounts/selectors';

//  MUI
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import transferMoney from 'assets/illustrations/Money-Movement.png';
import paperAirplaneCrash from 'assets/illustrations/Plane-Crash.png';
import LoadingView from 'components/LoadingView';
import QButton from 'components/QButton';
import AmountField from 'components/QuickenControls/AmountField';
//  CUSTOM COMPONENTS
import QTip from 'components/QuickenControls/QTip';
import ZeroStateView from 'components/ZeroStateView';
import {
  doesAccountSubTypeSupportA2a,
  isAccountA2aDepositOnly,
  isAccountEligibleForTransferringMoneyUtil,
} from 'data/fundingAccounts/utils';

const useStyles = makeStyles()((theme) => ({
  readyWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  amountField: {
    maxWidth: 200,
  },
  inputField: {
    textAlign: 'left',
  },
  toAccount: {
    marginBottom: 25,
  },
  fromAccount: {
    marginTop: 20,
    marginBottom: 25,
  },
  topText: {
    marginLeft: 7,
    fontSize: 18,
    marginBottom: 10,
  },
  transferMoneyIcon: {
    alignSelf: 'flex-start',
    width: 168,
    height: 'auto',
  },
  subHeader: {
    marginLeft: 5,
    padding: 10,
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  zeroStyles: {
    width: '100%',
    alignSelf: 'center',
    display: 'flex',
  },
  zeroStylesDialog: {
    paddingTop: theme.spacing(4),
    width: '100%',
    alignSelf: 'center',
    display: 'flex',
  },
  zeroStylesActivityPage: {
    paddingTop: theme.spacing(2),
    width: '100%',
    alignSelf: 'center',
    display: 'flex',
  },
  tryAgainButton: {
    marginTop: theme.spacing(2),
  },
  primaryFailureTextActivityPage: {
    marginBottom: theme.spacing(1),
  },
  primaryFailureTextDialog: {
    marginBottom: theme.spacing(2),
  },
  bottomButtons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    position: 'relative',
    height: 150,
  },
  verifyButton: {
    position: 'absolute',
    bottom: 0,
    width: 150,
  },
  amountRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  tertiaryText: {
    color: theme.palette.icon.light,
  },
  disabledText: {
    height: 44,
  },
}));

/**
 * Selector for grouping eligible **source** accounts by institution,
 * while moving deposit-only accounts to a separate 'Deposit-Only' group.
 */
export const getSourceTransferEligibleAccountsByInstitution = createSelector(
  [
    institutionLoginsSelectors.getInstitutionLoginsById,
    accountsSelectors.getAccountsById,
    fundingAccountsSelectors.getVerifiedFundingAccountsByAccountId,
  ],
  (institutionLogins, accountsById, verifiedFundingAccounts) => {
    return ImmutableMap<string, ImmutableList<accountsTypes.Account>>().withMutations((result) => {
      let depositOnlyAccounts = ImmutableList<accountsTypes.Account>();

      institutionLogins.forEach((institutionLogin) => {
        const accounts = accountsById.filter(
          (acc) => acc.institutionLoginId === institutionLogin.id && verifiedFundingAccounts.has(acc?.id || ''),
        );

        const { depositOnly, eligibleAccounts } = accounts.reduce(
          (accGroups, account) => {
            if (!isAccountA2aDepositOnly(account)) {
              accGroups.eligibleAccounts = accGroups.eligibleAccounts.push(account);
            } else if (isAccountA2aDepositOnly(account)) {
              accGroups.depositOnly = accGroups.depositOnly.push(account);
            }
            return accGroups;
          },
          {
            depositOnly: ImmutableList<accountsTypes.Account>(),
            eligibleAccounts: ImmutableList<accountsTypes.Account>(),
          },
        );

        // Store eligible source accounts under their institution
        if (!eligibleAccounts.isEmpty()) {
          result.set(institutionLogin.id, eligibleAccounts.toList());
        }

        // Accumulate deposit-only accounts (to be grouped separately)
        if (!depositOnly.isEmpty()) {
          depositOnlyAccounts = depositOnlyAccounts.concat(depositOnly);
        }
      });

      // Add Deposit-Only group if applicable
      if (!depositOnlyAccounts.isEmpty()) {
        result.set('Deposit-Only', depositOnlyAccounts);
      }
    });
  },
);

/**
 * Selector for grouping eligible **destination** accounts by institution,
 * allowing deposit-only accounts to remain in their respective institutions.
 */
export const getDestinationTransferEligibleAccountsByInstitution = createSelector(
  [
    institutionLoginsSelectors.getInstitutionLoginsById,
    accountsSelectors.getAccountsById,
    fundingAccountsSelectors.getVerifiedFundingAccountsByAccountId,
  ],
  (institutionLogins, accountsById, verifiedFundingAccounts) => {
    return ImmutableMap<string, ImmutableList<accountsTypes.Account>>().withMutations((result) => {
      institutionLogins.forEach((institutionLogin) => {
        const accounts = accountsById.filter(
          (acc) =>
            acc.institutionLoginId === institutionLogin.id &&
            verifiedFundingAccounts.has(acc?.id || '') &&
            doesAccountSubTypeSupportA2a(acc), // Includes deposit-only
        );

        if (!accounts.isEmpty()) {
          result.set(institutionLogin.id, accounts.toList());
        }
      });
    });
  },
);

// types
type TransferAccountSelectProps = {
  historyPage?: boolean;
  fromAccount?: accountsTypes.Account;
  setFromAccount: (account: accountsTypes.Account | undefined) => void;
  toAccount?: accountsTypes.Account;
  setToAccount: (account: accountsTypes.Account | undefined) => void;
  transferAmount?: number;
  setTransferAmount: (value: number) => void;
  setDisableContinue?: (shouldDisable: boolean) => void;
  onConfirm?: () => void;
  className?: string;
  onOptionsFetch?: (wasSuccess: boolean) => void;
};

const TransferAccountSelect = (props: TransferAccountSelectProps) => {
  const {
    historyPage,
    fromAccount,
    setFromAccount,
    toAccount,
    setToAccount,
    transferAmount = 0,
    setTransferAmount,
    setDisableContinue,
    onConfirm,
    className,
    onOptionsFetch,
  } = props;

  const { classes, cx } = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const kickoffA2AFlow = useKickoffA2AFlow();

  const verifiedFundingAccounts = useAppSelector(fundingAccountsSelectors.getVerifiedFundingAccountsByAccountId);

  const sourceEligibleAccounts = useAppSelector(getSourceTransferEligibleAccountsByInstitution);
  const destinationEligibleAccounts = useAppSelector(getDestinationTransferEligibleAccountsByInstitution);

  const fundingAccountQty = verifiedFundingAccounts.size;

  const institutionLogins = useAppSelector(institutionLoginsSelectors.getInstitutionLoginsById);

  const canUserMakeTransfers = useAppSelector(profileSelectors.isUserEligibleToMakeTransfers);

  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const today = useAppSelector(dateSelectors.getCurrentDateString);

  const a2AInvestments = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'a2AInvestmentAccounts'),
  );

  // ========= STATES ===========
  const [minimumAmount, setMinimumAmount] = useState<number | string>(1);
  const [maximumAmount, setMaximumAmount] = useState<number | string>(1000);
  const [internalDisableContinue, setInternalDisableContinue] = useState(true);
  const [forceLoading, setForceLoading] = useState(true);

  const view = useLoadState(fundingAccountsSelectors);
  const accountView = useLoadState(accountsSelectors);

  // Error possibility
  const tooFewAccounts = fundingAccountQty < 2;

  //  ViewStates
  const mainView = useMemo(() => {
    const optionsError = minimumAmount === 'error' || maximumAmount === 'error';

    const showSkeletonView = loadState.skeletonStates.includes(view) || loadState.skeletonStates.includes(accountView);
    const showTryAgainView =
      loadState.tryAgainStates.includes(view) ||
      loadState.tryAgainStates.includes(accountView) ||
      loadState.updateRequiredStates.includes(view) ||
      loadState.updateRequiredStates.includes(accountView) ||
      optionsError;
    const showNoDataView =
      loadState.noDataStates.includes(view) || loadState.noDataStates.includes(accountView) || tooFewAccounts; //  Do not include TransferOptions in noData state
    const showReadyView =
      loadState.readyStates.includes(view) &&
      loadState.readyStates.includes(accountView) &&
      !tooFewAccounts &&
      !optionsError;

    //  FAILED takes priority over EMPTY as there is a bug that causes one of the selectors to be marked as EMPTY when reloading the
    //  page actually fixes it and populates it with data.
    let main = 'FAILED';
    if (showReadyView) {
      main = 'READY';
    } else if (showTryAgainView) {
      main = 'FAILED';
    } else if (showNoDataView) {
      main = 'EMPTY';
    } else if (showSkeletonView) {
      main = 'LOADING';
    }

    return main;
  }, [accountView, maximumAmount, minimumAmount, tooFewAccounts, view]);

  // TODO: Add this to app-components
  const getAccountBalance = useCallback(
    (account: accountsTypes.Account | undefined) =>
      Number(
        typeof account?.aggregators?.first()?.availableBalance === 'number'
          ? account?.aggregators?.first()?.availableBalance
          : account?.aggregators?.first()?.onlineBalance,
      ) || 0,
    [],
  );

  const getMinMaxValues = useCallback(() => {
    const userTimeZone = DateTime.now().zoneName;
    const payload = {
      maxDays: 365,
      zoneId: userTimeZone,
      calendarStartDate: today,
      amount: '1.00',
    };
    setForceLoading(true);
    actionHelpers
      .bindPromiseAction(
        dispatch,
        a2aTransfersActions.getA2aTransferInfo,
      )(payload)
      .then((resp: any) => {
        if (onOptionsFetch) {
          onOptionsFetch(Boolean(Number(resp?.data?.minAmount) && Number(resp?.data?.maxAmount))); //success? True or false
        }
        setMinimumAmount(Number(resp?.data?.minAmount) || 'error');
        setMaximumAmount(Number(resp?.data?.maxAmount) || 'error');
        setForceLoading(false);
      })
      .catch((_error) => {
        if (onOptionsFetch) {
          onOptionsFetch(false); //success? True or false
        }
        setMinimumAmount('error');
        setMaximumAmount('error');
        setForceLoading(false);
      });
  }, [dispatch, onOptionsFetch, today]);

  const transferLimitError = useMemo(
    () => Number(transferAmount) > Number(maximumAmount),
    [maximumAmount, transferAmount],
  );
  const sameAccountError = useMemo(
    () => Boolean(fromAccount?.id === toAccount?.id && fromAccount && toAccount),
    [fromAccount, toAccount],
  );
  const biggerThanBalanceError = useMemo(
    () => transferAmount > getAccountBalance(fromAccount),
    [fromAccount, getAccountBalance, transferAmount],
  );
  const invalidNumberError = useMemo(
    () => Number(transferAmount) < 0 || typeof transferAmount !== 'number' || (transferAmount !== 0 && !transferAmount),
    [transferAmount],
  );
  const tooSmallNumberError = useMemo(
    () => Number(transferAmount) < Number(minimumAmount) && Number(transferAmount) !== 0,
    [minimumAmount, transferAmount],
  );
  const cannotGetDataError = useMemo(
    () => typeof maximumAmount !== 'number' || typeof minimumAmount !== 'number',
    [maximumAmount, minimumAmount],
  );

  const getTransferAmountErrorText = () => {
    if (forceLoading) {
      return 'Max limit: ';
    }
    let errorText = maximumAmount ? `Max limit: ${numberUtils.formatNumber(maximumAmount, 'USD', '0,0')}` : '';
    if (cannotGetDataError) {
      errorText = 'Error retrieving transfer limit data';
    } else if (invalidNumberError) {
      errorText = 'Invalid number';
    } else if (biggerThanBalanceError) {
      errorText = 'Not enough funds available in the selected account';
    } else if (transferLimitError) {
      errorText =
        typeof maximumAmount === 'number'
          ? `Max limit: ${numberUtils.formatNumber(maximumAmount, 'USD', '0,0')}`
          : 'Error retrieving data';
    } else if (tooSmallNumberError) {
      errorText =
        typeof minimumAmount === 'number'
          ? `Amount must be ${numberUtils.formatNumber(minimumAmount, 'USD', '0,0.00')} or more`
          : 'Error retrieving data';
    }
    return errorText;
  };

  const refreshData = useCallback(() => {
    dispatch(fundingAccountsActions.getFundingAccounts());
    dispatch(accountsActions.getAccounts());
    getMinMaxValues();
  }, [dispatch, getMinMaxValues]);

  //  ============= On First Render ============
  useEffect(() => {
    refreshData();
  }, [getMinMaxValues]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (setDisableContinue) {
      setDisableContinue(
        Boolean(
          !fromAccount ||
            !toAccount ||
            !transferAmount ||
            biggerThanBalanceError ||
            sameAccountError ||
            transferLimitError ||
            invalidNumberError ||
            tooSmallNumberError ||
            cannotGetDataError ||
            forceLoading,
        ),
      );
    }
    setInternalDisableContinue(
      Boolean(
        !fromAccount ||
          !toAccount ||
          !transferAmount ||
          biggerThanBalanceError ||
          sameAccountError ||
          transferLimitError ||
          invalidNumberError ||
          tooSmallNumberError ||
          cannotGetDataError ||
          forceLoading,
      ),
    );
  }, [
    biggerThanBalanceError,
    cannotGetDataError,
    fromAccount,
    invalidNumberError,
    sameAccountError,
    setDisableContinue,
    toAccount,
    tooSmallNumberError,
    transferAmount,
    transferLimitError,
    forceLoading,
  ]);

  const handleOpenSetup = useCallback(() => {
    kickoffA2AFlow({
      initiator: 'transfer activity page',
    });
  }, [kickoffA2AFlow]);

  const getSortedAccounts = (accountsMap: ImmutableMap<string, ImmutableList<accountsTypes.Account>>) => {
    return accountsMap.entrySeq().map(([institutionId, accounts], myIndex) => {
      const institutionName = institutionLogins?.get(institutionId)?.name ?? institutionId;
      const headerFormatting = (
        <div key={`${institutionName}-header`}>
          {myIndex !== 0 && <Divider />}
          <div className={classes.subHeader} id={institutionName}>
            {institutionName}
          </div>
        </div>
      );
      const menuItemFormatting = accounts.map((acc) => {
        const fundingAccount = verifiedFundingAccounts.get(acc?.id || '');
        if (!fundingAccount) return null;

        const accountEligibilityInfo = isAccountEligibleForTransferringMoneyUtil(
          acc,
          fundingAccount,
          institutionLogins.get(acc?.institutionLoginId || ''),
          !a2AInvestments,
        );

        const isDepositDisabled = institutionName === 'Deposit-Only';
        const isDisabled = !accountEligibilityInfo?.isEligible || isDepositDisabled;
        const disabledReason = isDepositDisabled
          ? 'Investment accounts are deposit-only and can’t be used as the “From account.”'
          : accountEligibilityInfo?.reasonString;

        if (isDisabled) {
          return (
            <QTip
              placement={'left'}
              solidTheme
              title={disabledReason}
              noTippyTip
              arrow
              enableOnMobile
              key={`${acc?.id}-tooltip`}
            >
              <MenuItem
                disabled={isDisabled}
                key={acc?.id ?? undefined}
                value={acc?.id ?? undefined}
                className={isDepositDisabled ? '' : classes.disabledText}
              >
                <ListItemText
                  primary={acc?.name}
                  secondary={isDepositDisabled ? <AmountField value={getAccountBalance(acc)} /> : null}
                />
              </MenuItem>
            </QTip>
          );
        } else {
          return (
            <MenuItem key={acc?.id ?? undefined} value={acc?.id ?? undefined}>
              <ListItemText primary={acc?.name} secondary={<AmountField value={getAccountBalance(acc)} />} />
            </MenuItem>
          );
        }
      });

      return [headerFormatting, menuItemFormatting];
    });
  };

  const handleChangeFrom = (e: SelectChangeEvent) => {
    const fromAccount = accountsById.get(e.target.value);
    setFromAccount(fromAccount);
  };

  const handleChangeTo = (e: SelectChangeEvent) => {
    const toAccount = accountsById.get(e.target.value);
    setToAccount(toAccount);
  };

  const failedToFetch = useMemo(
    () => (
      <Box pr={2} pl={2} pb={2} pt={3} width={'100%'}>
        {historyPage ? <Typography variant='titleMedium'>Transfer money</Typography> : null}
        <ZeroStateView
          className={historyPage ? classes.zeroStylesActivityPage : classes.zeroStylesDialog}
          button={
            <QButton className={classes.tryAgainButton} id='try-again-button' variant='outlined' onClick={refreshData}>
              TRY AGAIN
            </QButton>
          }
          id='failed-to-fetch'
          size='small'
          primary={
            <Typography
              variant={historyPage ? 'titleRegular' : 'headlineRegular'}
              align='center'
              className={historyPage ? classes.primaryFailureTextActivityPage : classes.primaryFailureTextDialog}
            >
              {historyPage ? 'Oops!' : 'Oops! Something went wrong'}
            </Typography>
          }
          secondaryVariant={historyPage ? 'paragraphRegular' : 'bodyRegular'}
          secondary={
            historyPage
              ? 'We were unable to load account transfers.'
              : 'We hit a snag and were unable to load account transfers.'
          }
          icon={paperAirplaneCrash}
          iconSize={{ width: historyPage ? 91.4 : 128, height: 'auto' }}
        />
      </Box>
    ),
    [classes, refreshData, historyPage],
  );

  const zeroState = useMemo(
    () =>
      historyPage ? (
        <div className={classes.readyWrapper}>
          <Box pr={2} pl={2} pb={2} pt={3} width={'100%'}>
            <Typography variant='titleMedium'>Transfer money</Typography>
            <ZeroStateView
              className={classes.zeroStyles}
              id='zero-state'
              primaryVariant='titleRegular'
              secondaryVariant='paragraphRegular'
              primary='Not enough accounts enabled'
              secondary='To transfer money, you’ll need to set up at least one more account for transfers.'
              icon={paperAirplaneCrash}
              iconSize={{ height: 120, width: 120 }}
              button={
                <QButton id='set-up-acct-button' variant='contained' onClick={handleOpenSetup}>
                  Set Up Another Account
                </QButton>
              }
            />
          </Box>
        </div>
      ) : (
        <ZeroStateView
          className={classes.zeroStyles}
          id='zero-state'
          primaryVariant='headlineRegular'
          primary='Not enough accounts enabled'
          secondary='To transfer money, you’ll need to set up at least one more account for transfers. Visit Account Settings to see the eligibility of all your accounts.'
          icon={paperAirplaneCrash}
        />
      ),
    [historyPage, classes, handleOpenSetup],
  );

  return (
    <div
      className={
        historyPage
          ? cx(className, (tooFewAccounts && mainView === 'EMPTY') || !canUserMakeTransfers ? 'hidden' : '')
          : ''
      }
    >
      {mainView === 'FAILED' && failedToFetch}

      {mainView === 'LOADING' && (
        <Box
          paddingTop={20}
          paddingBottom={20}
          display='flex'
          alignItems='center'
          flexDirection='column'
          justifyContent='center'
        >
          <LoadingView size={100} />
        </Box>
      )}

      {mainView === 'EMPTY' && zeroState}

      {mainView === 'READY' && (
        <div className={classes.readyWrapper}>
          {!historyPage && <img src={transferMoney} alt='transfer-cards' className={classes.transferMoneyIcon} />}
          <Box pr={2} pl={2} pb={2} pt={3} width={'100%'}>
            {historyPage ? (
              <Typography variant='titleMedium'>Transfer money</Typography>
            ) : (
              <Typography className={classes.topText}>Enter the details of your transfer.</Typography>
            )}

            {/* From Account Selection */}
            <Box className={classes.fromAccount} display='flex' alignItems='center' my={1}>
              <FormControl variant='filled' sx={{ width: historyPage ? '100%' : 400 }}>
                <InputLabel variant='filled' id='account-transfer-from-select-label'>
                  From account
                </InputLabel>
                <Select
                  labelId='account-transfer-from-select-label'
                  id='account-transfer-from-select'
                  label='From account'
                  variant='filled'
                  onChange={handleChangeFrom}
                  value={fromAccount?.id || ''}
                  sx={{
                    backgroundColor: theme.palette.background.default,
                    '.MuiSelect-icon': {
                      color: theme.palette.icon.light,
                    },
                  }}
                  renderValue={(accountId) => (
                    <Box display='flex' flexDirection='row' justifyContent='space-between'>
                      <Typography variant='bodyRegular' noWrap>
                        {accountsById?.get(accountId)?.name}
                      </Typography>
                      {fromAccount && typeof getAccountBalance(fromAccount) === 'number' && (
                        <Typography variant='paragraphRegular' className={classes.tertiaryText}>
                          {numberUtils.formatNumber(getAccountBalance(fromAccount), 'USD', '0,0.00')}
                        </Typography>
                      )}
                    </Box>
                  )}
                >
                  {getSortedAccounts(sourceEligibleAccounts)}
                </Select>
              </FormControl>
            </Box>

            {/* To Account Selection */}
            <Box className={classes.toAccount} display='flex' alignItems='center' my={1}>
              <FormControl variant='filled' sx={{ width: historyPage ? '100%' : 400 }} error={sameAccountError}>
                <InputLabel variant='filled' id='account-transfer-to-select-label'>
                  To account
                </InputLabel>
                <Select
                  labelId='account-transfer-to-select-label'
                  id='account-transfer-to-select'
                  label='To account'
                  variant='filled'
                  onChange={handleChangeTo}
                  value={toAccount?.id || ''}
                  sx={{
                    backgroundColor: theme.palette.background.default,
                    '.MuiSelect-icon': {
                      color: theme.palette.icon.light,
                    },
                  }}
                  renderValue={(accountId) => (
                    <Box display='flex' flexDirection='row' justifyContent='space-between'>
                      <Typography variant='bodyRegular' noWrap>
                        {accountsById?.get(accountId)?.name}
                      </Typography>
                      {toAccount && !sameAccountError && typeof getAccountBalance(toAccount) === 'number' && (
                        <Typography variant='paragraphRegular' className={classes.tertiaryText}>
                          {numberUtils.formatNumber(getAccountBalance(toAccount), 'USD', '0,0.00')}
                        </Typography>
                      )}
                    </Box>
                  )}
                >
                  {getSortedAccounts(destinationEligibleAccounts)}
                </Select>
                {sameAccountError && <FormHelperText>{'Cannot transfer to the same account'}</FormHelperText>}
              </FormControl>
            </Box>
            <div className={classes.bottomButtons}>
              <div className={classes.amountRow}>
                <AmountField
                  showAmountAdornment
                  className={classes.amountField}
                  classes={{ inputField: classes.inputField }}
                  value={transferAmount}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTransferAmount(Number(e.target.value))}
                  textFieldVariant='filled'
                  editable
                  label='Amount'
                  error={
                    transferLimitError ||
                    biggerThanBalanceError ||
                    invalidNumberError ||
                    tooSmallNumberError ||
                    cannotGetDataError
                  }
                  helperText={getTransferAmountErrorText()}
                />
                {forceLoading && (
                  <Box marginTop={-1}>
                    <LoadingView size={35} />
                  </Box>
                )}
              </div>
              {historyPage && (
                <QButton
                  className={classes.verifyButton}
                  variant='contained'
                  id='confirm-transfer-button'
                  disabled={internalDisableContinue}
                  onClick={onConfirm}
                  color='primary'
                >
                  Review & Send
                </QButton>
              )}
            </div>
          </Box>
        </div>
      )}
    </div>
  );
};

export default TransferAccountSelect;
